import React, { useState, useMemo } from 'react';
import { Search, Plus, Edit, Trash2, Package, AlertTriangle, TrendingUp } from 'lucide-react';

// Components
import ItemModal from './ItemModal';
import Toast from './Toast';

// Helpers
import { formatCurrency, formatDateTime } from '../Helpers';
import { deleteFeedback, undoFeedback } from '../utils/feedback';


const InventoryList = ({ inventory, onAdd, onUpdate, onDelete, onRestock }) => {
    const LOW_STOCK_QUANITTY = 5;
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [sortKey, setSortKey] = useState('lastUpdated');
    const [sortDirection, setSortDirection] = useState('desc');
    const [toast, setToast] = useState(null);
    // const [isMobileView, setIsMobileView] = useState(false);

    // const filteredInventory = inventory.filter(item =>
    //     item.itemName.toLowerCase().includes(searchTerm.toLowerCase())
    // );

    const filteredInventory = useMemo(() => {
        return inventory.filter(item =>
            item.itemName.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [inventory, searchTerm]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };

    const getSortIndicator = (key) => {
        if (sortKey === key) {
            return sortDirection === 'asc' ? '▲' : '▼';
        }
        return '';
    };

    const sortedInventory = useMemo(() => {
        if (!sortKey) return filteredInventory;

        const sorted = [...filteredInventory].sort((a, b) => {
            let aValue = a[sortKey];
            let bValue = b[sortKey];

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [filteredInventory, sortKey, sortDirection]);

    const handleOpenModal = (item = null, isRestock = false) => {
        setCurrentItem(item ? { ...item, isRestock } : { isRestock });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setCurrentItem(null);
        setIsModalOpen(false);
    };

    const handleSave = (item) => {
        if (currentItem.isRestock) {
            onRestock(currentItem.id, parseInt(item.quantity, 10));
        } else if (currentItem.id) {
            onUpdate(currentItem.id, item);
        } else {
            onAdd(item);
        }
        handleCloseModal();
    };

    const confirmDelete = (itemId) => {
        const deletedItem = inventory.find(item => item.id === itemId);
        onDelete(itemId);
        deleteFeedback();

        setToast({
            id: Date.now(),
            message: `"${deletedItem?.itemName}" deleted`,
            type: 'delete',
            duration: 5000,
            undoAction: () => {
                // Re-add the item to restore it
                if (deletedItem) {
                    const { id, ...itemData } = deletedItem;
                    onAdd(itemData);
                    undoFeedback();
                }
            }
        });
    };

    // Handle window resize to determine mobile view
    // useEffect(() => {
    //     const handleResize = () => {
    //         setIsMobileView(window.innerWidth < 768);
    //     };
    //     window.addEventListener('resize', handleResize);
    //     handleResize(); // Initial check
    //     return () => window.removeEventListener('resize', handleResize);
    // }, []);

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-4 sm:p-8 text-white">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center space-x-4">
                        <div className="bg-white bg-opacity-20 rounded-full p-3 sm:p-4">
                            <Package className="h-8 w-8 sm:h-12 sm:w-12" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold">Inventory Management</h1>
                            <p className="text-green-100 mt-1 text-sm sm:text-base">Manage your products and stock levels</p>
                        </div>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="hidden sm:flex bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-200 items-center space-x-2 text-sm sm:text-base"
                    >
                        <Plus size={16} className="sm:w-5 sm:h-5" />
                        <span>Add Item</span>
                    </button>
                </div>
            </div>

            {/* Search */}
            {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"> */}
            <div className="space-y-3">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search for items..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-3 sm:p-4 pl-10 sm:pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm sm:text-base"
                    />
                    <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>

                {/* Mobile Sorting Pills */}
                <div className="lg:hidden flex flex-wrap gap-2">
                    {[
                        { key: 'itemName', label: 'Name' },
                        { key: 'lastUpdated', label: 'Date' },
                        { key: 'sellingPrice', label: 'Price' },
                        { key: 'quantity', label: 'Stock' },
                        { key: 'category', label: 'Category' }
                    ].map(sort => (
                        <button
                            key={sort.key}
                            onClick={() => handleSort(sort.key)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${sortKey === sort.key
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            {sort.label} {sortKey === sort.key && (sortDirection === 'asc' ? '↑' : '↓')}
                        </button>
                    ))}
                </div>
            </div>
            {/* </div> */}

            {/* Inventory Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <Package className="h-5 w-5 mr-2 text-green-600" />
                        Inventory Items ({sortedInventory.length})
                    </h2>
                </div>
                {sortedInventory.length === 0 ? (
                    <div className="text-center py-12">
                        <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No items found</h3>
                        <p className="text-gray-500 mb-6">
                            {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first inventory item'}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() => handleOpenModal()}
                                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                            >
                                Add First Item
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="p-4 font-medium text-gray-600 cursor-pointer">
                                            <button onClick={() => handleSort('itemName')} className="flex items-center gap-1 hover:text-gray-900">
                                                Item Name {getSortIndicator('itemName')}
                                            </button>
                                        </th>
                                        <th className="p-4 font-medium text-gray-600 cursor-pointer">
                                            <button onClick={() => handleSort('category')} className="flex items-center gap-1 hover:text-gray-900">
                                                Category {getSortIndicator('category')}
                                            </button>
                                        </th>
                                        <th className="p-4 font-medium text-gray-600">Purchase Price</th>
                                        <th className="p-4 font-medium text-gray-600">Selling Price</th>
                                        <th className="p-4 font-medium text-gray-600 cursor-pointer">
                                            <button onClick={() => handleSort('quantity')} className="flex items-center gap-1 hover:text-gray-900">
                                                Quantity {getSortIndicator('quantity')}
                                            </button>
                                        </th>
                                        <th className="p-4 font-medium text-gray-600">
                                            <button onClick={() => handleSort('lastUpdated')} className="flex items-center gap-1 hover:text-gray-900">
                                                Last Updated {getSortIndicator('lastUpdated')}
                                            </button>
                                        </th>
                                        <th className="p-4 font-medium text-gray-600">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedInventory.map(item => (
                                        <tr key={item.id} className="border-b hover:bg-gray-50 transition-colors">
                                            <td className="p-4">
                                                <div className="flex items-center space-x-3">
                                                    <div className="bg-gray-100 rounded-lg p-2">
                                                        <Package className="h-5 w-5 text-gray-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-gray-900">{item.itemName}</p>
                                                        {item.quantity <= LOW_STOCK_QUANITTY && (
                                                            <div className="flex items-center mt-1">
                                                                <AlertTriangle className="h-3 w-3 text-red-500 mr-1" />
                                                                <span className="text-xs text-red-600">Low Stock</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4 text-gray-600">{item.category}</td>
                                            <td className="p-4 text-gray-600">{formatCurrency(item.purchasePrice)}</td>
                                            <td className="p-4 text-gray-600 font-medium">{formatCurrency(item.sellingPrice)}</td>
                                            <td className="p-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.quantity <= LOW_STOCK_QUANITTY
                                                    ? 'bg-red-100 text-red-800'
                                                    : item.quantity <= 50
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-green-100 text-green-800'
                                                    }`}>
                                                    {item.quantity}
                                                </span>
                                            </td>
                                            <td className="p-4 text-gray-500 text-sm">{formatDateTime(item.lastUpdated)}</td>
                                            <td className="p-4">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => handleOpenModal(item)}
                                                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                                        title="Edit Item"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenModal(item, true)}
                                                        className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                                        title="Restock Item"
                                                    >
                                                        <TrendingUp size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => confirmDelete(item.id)}
                                                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                        title="Delete Item"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="lg:hidden space-y-4 p-4">
                            {sortedInventory.map(item => (
                                <div key={item.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center space-x-3">
                                            <div className="bg-gray-100 rounded-lg p-2">
                                                <Package className="h-5 w-5 text-gray-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-900">{item.itemName}</p>
                                                <p className="text-sm text-gray-600">{item.category}</p>
                                                {item.quantity <= LOW_STOCK_QUANITTY && (
                                                    <div className="flex items-center mt-1">
                                                        <AlertTriangle className="h-3 w-3 text-red-500 mr-1" />
                                                        <span className="text-xs text-red-600">Low Stock</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.quantity <= LOW_STOCK_QUANITTY
                                            ? 'bg-red-100 text-red-800'
                                            : item.quantity <= 50
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-green-100 text-green-800'
                                            }`}>
                                            {item.quantity}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                                        <div>
                                            <p className="text-gray-500">Purchase Price</p>
                                            <p className="font-medium">{formatCurrency(item.purchasePrice)}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Selling Price</p>
                                            <p className="font-medium">{formatCurrency(item.sellingPrice)}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <p className="text-xs text-gray-500">{formatDateTime(item.lastUpdated)}</p>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => handleOpenModal(item)}
                                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                                title="Edit Item"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleOpenModal(item, true)}
                                                className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                                title="Restock Item"
                                            >
                                                <TrendingUp size={16} />
                                            </button>
                                            <button
                                                onClick={() => confirmDelete(item.id)}
                                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                title="Delete Item"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Mobile Floating Add Button */}
            <button
                onClick={() => handleOpenModal()}
                className="sm:hidden fixed bottom-24 right-6 bg-green-600 hover:bg-green-700 text-white p-4 rounded-full shadow-lg transition-all duration-200 z-40"
            >
                <Plus size={24} />
            </button>

            {isModalOpen && <ItemModal item={currentItem} onSave={handleSave} onClose={handleCloseModal} />}

            {/* Toast Notification */}
            {toast && (
                <Toast
                    key={toast.id}
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    undoAction={toast.undoAction}
                    onDismiss={() => setToast(null)}
                />
            )}
        </div>
    );


};

export default InventoryList;